import { useCallback, useMemo, useState } from 'react';

import { defaultCentre, type GeoCentre } from './defaultCentre';

/**
 * Where the current map centre came from. The UI says this out loud, because a map silently
 * showing the wrong city is indistinguishable from a broken map.
 */
export type CentreSource = 'default' | 'device' | 'search';

/** What the device-location request is currently doing. */
export type LocateStatus =
  | 'idle'
  | 'locating'
  /** The browser refused: the user denied, or the site is blocked in site settings. */
  | 'denied'
  /** Timed out, position unavailable, or the browser has no geolocation at all. */
  | 'unavailable';

/** The map centre and the controls that move it. */
export interface MapCentreState {
  /** The centre to search around. */
  readonly centre: GeoCentre;
  /** Where that centre came from. */
  readonly source: CentreSource;
  /** The state of the last device-location attempt. */
  readonly status: LocateStatus;
  /** Whether this browser exposes the Geolocation API at all. */
  readonly canLocate: boolean;
  /** Asks the browser for the device position. Only call from a user gesture. */
  readonly requestDeviceLocation: () => void;
  /** Moves the centre to a place the user picked from search. */
  readonly setSearchedLocation: (centre: GeoCentre) => void;
  /** Returns to the launch metro. */
  readonly reset: () => void;
}

/**
 * How long to wait for a position.
 *
 * Ten seconds rather than the browser default, because this is now user-initiated: they clicked
 * and are watching a spinner, and a cold GPS fix on a phone routinely needs more than five.
 */
const POSITION_TIMEOUT_MS = 10_000;

/** Accept a cached fix up to five minutes old; nobody moves far enough for it to matter. */
const POSITION_MAX_AGE_MS = 300_000;

/**
 * Owns the map centre and the chain that resolves it: a searched location wins, then the device
 * position, then the launch metro.
 *
 * Nothing is persisted, deliberately. No `localStorage`, no cookie, nothing sent to the server —
 * a resolved position lives in React state for the life of the page and then it is gone. That
 * keeps the privacy policy's claim about location honest, at the cost of the visitor re-locating
 * on each visit.
 *
 * The device position is never requested on mount. Browsers penalise unsolicited permission
 * prompts, Chrome auto-blocks the site for anyone who has dismissed one before, and a denial is
 * sticky — so the prompt is tied to a click instead.
 *
 * @returns The current centre and the controls that change it.
 */
export function useMapCentre(): MapCentreState {
  const fallback = useMemo(() => defaultCentre(), []);

  const [centre, setCentre] = useState<GeoCentre>(fallback);
  const [source, setSource] = useState<CentreSource>('default');
  const [status, setStatus] = useState<LocateStatus>('idle');

  const canLocate =
    typeof navigator !== 'undefined' && typeof navigator.geolocation?.getCurrentPosition === 'function';

  const requestDeviceLocation = useCallback(() => {
    if (!canLocate) {
      setStatus('unavailable');
      return;
    }

    setStatus('locating');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCentre({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          label: 'your location',
        });
        setSource('device');
        setStatus('idle');
      },
      (error) => {
        // Separating a denial from a failure matters: a denial is sticky and the visitor has to
        // change it in site settings, so telling them "we could not get your location" would send
        // them round in circles clicking a button that can no longer work.
        setStatus(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { timeout: POSITION_TIMEOUT_MS, maximumAge: POSITION_MAX_AGE_MS, enableHighAccuracy: false },
    );
  }, [canLocate]);

  const setSearchedLocation = useCallback((next: GeoCentre) => {
    setCentre(next);
    setSource('search');
    setStatus('idle');
  }, []);

  const reset = useCallback(() => {
    setCentre(fallback);
    setSource('default');
    setStatus('idle');
  }, [fallback]);

  return {
    centre,
    source,
    status,
    canLocate,
    requestDeviceLocation,
    setSearchedLocation,
    reset,
  };
}
